import { useEffect, useState } from "react"
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  AlertCircle,
  CheckCircle,
} from "lucide-react"
import {
  getDiagnosis,
  getDiagnosisHistory,
  triggerDiagnosis,
  type DiagnosisResult,
} from "../../api/aiTeam"

export default function DiagnosisPage() {
  const [diagnosis, setDiagnosis] = useState<DiagnosisResult | null>(null)
  const [history, setHistory] = useState<DiagnosisResult[]>([])
  const [loading, setLoading] = useState(true)
  const [diagnosing, setDiagnosing] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    try {
      const [diag, hist] = await Promise.all([
        getDiagnosis().catch(() => null),
        getDiagnosisHistory(5).catch(() => []),
      ])
      setDiagnosis(diag)
      setHistory(hist)
    } catch (err) {
      console.error("Failed to load diagnosis:", err)
    } finally {
      setLoading(false)
    }
  }

  async function handleDiagnose() {
    setDiagnosing(true)
    try {
      await triggerDiagnosis({ source: "manual" })
      await loadData()
    } catch (err) {
      console.error("Diagnosis failed:", err)
    } finally {
      setDiagnosing(false)
    }
  }

  const levelLabels: Record<string, string> = {
    weak: "待提升",
    medium: "中等",
    strong: "优秀",
  }

  const levelColors: Record<string, string> = {
    weak: "border-red-200 bg-red-50",
    medium: "border-yellow-200 bg-yellow-50",
    strong: "border-green-200 bg-green-50",
  }

  const severityColors: Record<string, string> = {
    high: "bg-red-100 text-red-700",
    medium: "bg-yellow-100 text-yellow-700",
    low: "bg-blue-100 text-blue-700",
  }

  const TrendIcon = ({ trend }: { trend: string }) => {
    if (trend === "improving")
      return <TrendingUp className="w-4 h-4 text-green-500" />
    if (trend === "declining")
      return <TrendingDown className="w-4 h-4 text-red-500" />
    return <Minus className="w-4 h-4 text-slate-400" />
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">学习诊断</h1>
          <p className="text-slate-500 mt-1">了解你的学习水平和薄弱点</p>
        </div>
        <Button onClick={handleDiagnose} disabled={diagnosing}>
          {diagnosing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              诊断中...
            </>
          ) : (
            "重新诊断"
          )}
        </Button>
      </div>

      {diagnosis ? (
        <div className="space-y-6">
          {/* Overall Level */}
          <Card
            className={`border-2 ${
              levelColors[diagnosis.result.overallLevel] ?? levelColors.medium
            }`}
          >
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-slate-500 mb-2">整体水平</p>
                <span className="text-4xl font-bold">
                  {levelLabels[diagnosis.result.overallLevel] ?? "中等"}
                </span>
                <p className="text-xs text-slate-400 mt-2">
                  诊断时间:{" "}
                  {new Date(diagnosis.created_at).toLocaleString("zh-CN")}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Subject Breakdown */}
          {diagnosis.result.subjectBreakdown?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">学科分析</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {diagnosis.result.subjectBreakdown.map((subject) => (
                    <div key={subject.subject} className="p-4 rounded-lg bg-slate-50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{subject.subject}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-bold">
                            {subject.score}
                          </span>
                          <span className="text-sm text-slate-500">分</span>
                          <TrendIcon trend={subject.trend} />
                        </div>
                      </div>

                      {subject.weakTopics?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-slate-500 mb-1">
                            薄弱知识点:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {subject.weakTopics.map((topic) => (
                              <span
                                key={topic}
                                className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded"
                              >
                                {topic}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {subject.strongTopics?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-slate-500 mb-1">
                            优势知识点:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {subject.strongTopics.map((topic) => (
                              <span
                                key={topic}
                                className="text-xs px-2 py-1 bg-green-50 text-green-600 rounded"
                              >
                                {topic}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Weak Points */}
          {diagnosis.result.weakPoints?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">薄弱知识点详情</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {diagnosis.result.weakPoints.map((wp) => (
                    <div
                      key={wp.kpCode}
                      className="p-3 rounded-lg border border-slate-200"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">{wp.name}</span>
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            severityColors[wp.severity] ?? severityColors.medium
                          }`}
                        >
                          {wp.severity === "high"
                            ? "高"
                            : wp.severity === "medium"
                            ? "中"
                            : "低"}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{wp.evidence}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">
                还没有诊断记录
              </h3>
              <p className="text-slate-500 mb-6">
                点击"开始诊断"按钮，AI 将分析你的学习情况
              </p>
              <Button onClick={handleDiagnose} disabled={diagnosing}>
                {diagnosing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    诊断中...
                  </>
                ) : (
                  "开始诊断"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History */}
      {history.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">诊断历史</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-50"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`text-xs px-2 py-1 rounded ${
                        levelColors[item.result.overallLevel] ?? levelColors.medium
                      }`}
                    >
                      {levelLabels[item.result.overallLevel] ?? "中等"}
                    </span>
                    <span className="text-sm text-slate-600">
                      {item.diagnosis_type === "upload"
                        ? "上传诊断"
                        : item.diagnosis_type === "auto"
                        ? "自动诊断"
                        : "手动诊断"}
                    </span>
                  </div>
                  <span className="text-xs text-slate-400">
                    {new Date(item.created_at).toLocaleDateString("zh-CN")}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
